import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../../styles/theme';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease-out forwards;
  padding: 1rem;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
    align-items: flex-start;
    padding-top: 2rem;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 0;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  position: relative;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.4);
  text-align: center;
  animation: slideIn 0.3s ease-out forwards;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;

  @keyframes slideIn {
    from {
      transform: scale(0.9) translateY(-20px);
      opacity: 0;
    }
    to {
      transform: scale(1) translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    max-height: 95vh;
    border-radius: 12px;
    margin: 0;
  }

  @media (max-width: 480px) {
    border-radius: 8px;
    max-height: 98vh;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 280px;
  overflow: hidden;
  margin-bottom: 0;
  position: relative;
  background: #f5f5f5;
  border-radius: 16px 16px 0 0;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
    display: block;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.02);
  }
  
  @media (max-width: 768px) {
    height: 200px;
    border-radius: 12px 12px 0 0;
  }

  @media (max-width: 480px) {
    height: 180px;
    border-radius: 8px 8px 0 0;
  }
`;

const ContentWrapper = styled.div`
  padding: 2rem 2.5rem 3rem;
  flex: 1;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem 2rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem 1.25rem 1.75rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  font-size: 1.25rem;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s ease;
  opacity: 0.9;
  z-index: 10;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  
  &:hover {
    opacity: 1;
    background-color: white;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    top: 0.75rem;
    right: 0.75rem;
    width: 32px;
    height: 32px;
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    top: 0.5rem;
    right: 0.5rem;
    width: 30px;
    height: 30px;
    font-size: 1rem;
  }
`;

const Title = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.75rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
    line-height: 1.25;
  }
`;

const Message = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1rem;
  color: ${props => props.theme.colors.primary};
  line-height: 1.7;
  margin-bottom: 1.5rem;
  opacity: 0.85;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 1.25rem;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
    line-height: 1.5;
  }
`;

const SubMessage = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 0.95rem;
  color: ${props => props.theme.colors.primary};
  line-height: 1.6;
  margin-bottom: 1.5rem;
  opacity: 0.75;
  font-style: italic;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 1.25rem;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin-bottom: 1rem;
    line-height: 1.4;
  }
`;

const EventInfo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding: 1.25rem 0;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    padding: 1rem 0;
    gap: 1rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.25rem;
    padding: 0.875rem 0;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.8;

  @media (max-width: 480px) {
    font-size: 0.9rem;
    gap: 0.4rem;
  }
`;

const PriceTag = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 1.25rem;
  }
`;

const BookButton = styled.a`
  display: inline-block;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
  width: 100%;
  max-width: 300px;
  margin-bottom: 1rem;
  
  &:hover {
    background: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    padding: 0.875rem 2rem;
    font-size: 0.95rem;
    max-width: 100%;
    margin-bottom: 0.875rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
    border-radius: 6px;
    margin-bottom: 0.75rem;
  }
`;

const CloseButtonBelow = styled.button`
  display: inline-block;
  background: transparent;
  color: ${props => props.theme.colors.primary};
  padding: 0.75rem 2rem;
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: 8px;
  font-family: ${props => props.theme.fonts.main};
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
  width: 100%;
  max-width: 300px;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    padding: 0.625rem 1.75rem;
    font-size: 0.9rem;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 1.5rem;
    font-size: 0.85rem;
    border-radius: 6px;
  }
`;

const ChristmasMediumshipPopup = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenShown, setHasBeenShown] = useState(false);

  useEffect(() => {
    // Don't show popup on booking journey pages or any event pages
    if (location.pathname.includes('/booking-journey') || location.pathname.includes('/event')) {
      setIsVisible(false);
      setHasBeenShown(true);
      return;
    }

    // Check if popup has already been shown in this session
    const popupShown = sessionStorage.getItem('christmasMediumshipPopupDec2025');
    if (popupShown) {
      setHasBeenShown(true);
      return;
    }

    // Show popup from 1st November 2025 until 13th December 2025
    const today = new Date();
    const startDate = new Date(2025, 10, 1); // November 1, 2025 (month is 0-based)
    const endDate = new Date(2025, 11, 13); // December 13, 2025 (month is 0-based)
    
    const isWithinDateRange = today >= startDate && today <= endDate;

    if (isWithinDateRange && !hasBeenShown) {
      setIsVisible(true);
    }
  }, [hasBeenShown, location.pathname]);

  const handleClose = () => {
    setIsVisible(false);
    setHasBeenShown(true);
    // Remember that popup was shown for this session
    sessionStorage.setItem('christmasMediumshipPopupDec2025', 'true');
  };

  // Don't show popup on booking journey pages, any event pages, or if not visible/has been shown
  if (location.pathname.includes('/booking-journey') || 
      location.pathname.includes('/event') ||
      !isVisible || 
      hasBeenShown) {
    return null;
  }

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        <ImageContainer>
          <img 
            src="https://www.belvoircastle.com/wp/wp-content/uploads/2025/12/IMG_1704.jpg" 
            alt="A Night of Festive Reflection with Her Grace"
            loading="eager"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </ImageContainer>
        <ContentWrapper>
          <Title>A Night of Festive Reflection<br />with Her Grace</Title>
          <Message>
            An intimate evening at the Castle, beautifully decorated for Christmas, with Her Grace, The Duchess of Rutland and Krista, PhD.
          </Message>
          <SubMessage>
            Experience a candlelit tour through historic rooms, refreshments in the Aviary Tea Room, and a group mediumship demonstration—an opportunity to reconnect with loved ones in a small, intimate setting.
          </SubMessage>
          
          <EventInfo>
            <InfoItem>
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>13 December 2025</span>
            </InfoItem>
          </EventInfo>

          <PriceTag>£30 per person</PriceTag>

          <BookButton 
            href="https://www.belvoircastle.com/event/cG9zdDozODI=" 
            target="_blank" 
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          >
            Book Now
          </BookButton>
          
          <CloseButtonBelow onClick={handleClose}>
            Close
          </CloseButtonBelow>
        </ContentWrapper>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ChristmasMediumshipPopup;

